/**
 * Session Manager Service
 *
 * Manages session-wide data including the active chatRoomId.
 * This ensures consistency across navigation and prevents multiple chatRoomIds
 * from being used within a single session.
 */

// Storage keys
const SESSION_CHAT_ROOM_ID = 'sessionChatRoomId';
const SESSION_START_TIME = 'sessionStartTime';

/**
 * Get the active chat room ID for this session
 * @returns {string|null} The active chat room ID or null if not set
 */
export const getSessionChatRoomId = () => {
  try {
    return sessionStorage.getItem(SESSION_CHAT_ROOM_ID);
  } catch (error) {
    console.error('[SESSION] Error getting session chatRoomId:', error);
    return null;
  }
};

/**
 * Set the active chat room ID for this session
 * For prototype: Always replaces existing sessions by default (force=true)
 * This makes testing easier - each new QR scan starts a fresh session
 *
 * @param {string} chatRoomId - The chat room ID to set as the session ID
 * @param {boolean} force - If true, will override any existing session chatRoomId (default: true for prototype)
 * @returns {boolean} True if set successfully, false if not (already exists and force=false)
 */
export const setSessionChatRoomId = (chatRoomId, force = true) => {
  if (!chatRoomId) return false;

  try {
    const existingChatRoomId = getSessionChatRoomId();

    // If force is true OR there's no existing chatRoomId, set it
    if (force || !existingChatRoomId) {
      sessionStorage.setItem(SESSION_CHAT_ROOM_ID, chatRoomId);

      // If force=true, reset session start time (new session)
      // Otherwise, only set if not already present (preserving existing session)
      if (force) {
        sessionStorage.setItem(SESSION_START_TIME, new Date().toISOString());
        console.log('[SESSION] Reset session start time (force=true)');
      } else if (!sessionStorage.getItem(SESSION_START_TIME)) {
        sessionStorage.setItem(SESSION_START_TIME, new Date().toISOString());
        console.log('[SESSION] Set initial session start time');
      }

      console.log(`[SESSION] Set session chatRoomId: ${chatRoomId}`);
      return true;
    }

    console.log(`[SESSION] Not setting session chatRoomId (already exists): ${existingChatRoomId}`);
    return false;
  } catch (error) {
    console.error('[SESSION] Error setting session chatRoomId:', error);
    return false;
  }
};

/**
 * Clear the active chat room ID and session data
 * Use this when ending a session or logging out
 */
export const clearSessionData = () => {
  try {
    sessionStorage.removeItem(SESSION_CHAT_ROOM_ID);
    sessionStorage.removeItem(SESSION_START_TIME);
    console.log('[SESSION] Cleared session data');
    return true;
  } catch (error) {
    console.error('[SESSION] Error clearing session data:', error);
    return false;
  }
};

/**
 * Get the session start time
 * @returns {string|null} ISO timestamp string of when the session started
 */
export const getSessionStartTime = () => {
  try {
    return sessionStorage.getItem(SESSION_START_TIME);
  } catch (error) {
    console.error('[SESSION] Error getting session start time:', error);
    return null;
  }
};

/**
 * Get the session duration in milliseconds
 * @returns {number} Duration in milliseconds or 0 if no start time
 */
export const getSessionDuration = () => {
  try {
    const startTime = sessionStorage.getItem(SESSION_START_TIME);
    if (!startTime) return 0;

    return Date.now() - new Date(startTime).getTime();
  } catch (error) {
    console.error('[SESSION] Error calculating session duration:', error);
    return 0;
  }
};
